// Fichier: src/pages/accounting/JournalEntries.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, Button, IconButton, Chip, Tooltip, TextField,
  MenuItem, InputAdornment, Snackbar, Alert
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, LockOpen as LockOpenIcon,
  ArrowBack as ArrowBackIcon, Search as SearchIcon
} from '@mui/icons-material';
import { db } from '../../firebase';
import { collection, onSnapshot, query, orderBy, doc, deleteDoc, setDoc } from 'firebase/firestore';

const JournalEntries = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [yearFilter, setYearFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    const q = query(collection(db, 'accounting_entries'), orderBy('date', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error(err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const years = useMemo(() => {
    const set = new Set(entries.map((e) => e.exerciceId).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [entries]);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      const matchYear = yearFilter === 'all' || e.exerciceId === yearFilter;
      const matchStatus = statusFilter === 'all' || e.status === statusFilter;
      const s = search.trim().toLowerCase();
      const matchSearch =
        !s || e.reference?.toLowerCase().includes(s) || e.description?.toLowerCase().includes(s);
      return matchYear && matchStatus && matchSearch;
    });
  }, [entries, yearFilter, statusFilter, search]);

  const formatDate = (d) => {
    const date = d?.toDate ? d.toDate() : new Date(d);
    return date.toLocaleDateString('fr-BE');
  };

  const handleDelete = async (entry) => {
    if (!window.confirm(`Supprimer l'écriture ${entry.reference} ?`)) return;
    try {
      await deleteDoc(doc(db, 'accounting_entries', entry.id));
      setSnackbar({ open: true, message: 'Écriture supprimée.', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Erreur lors de la suppression.', severity: 'error' });
    }
  };

  const handleUnvalidate = async (entry) => {
    if (!window.confirm(`Dévalider l'écriture ${entry.reference} pour la corriger ?`)) return;
    try {
      await setDoc(doc(db, 'accounting_entries', entry.id), { status: 'brouillon', updatedAt: new Date() }, { merge: true });
      setSnackbar({ open: true, message: 'Écriture repassée en brouillon.', severity: 'success' });
    } catch (err) {
      console.error(err);
      setSnackbar({ open: true, message: 'Erreur.', severity: 'error' });
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton onClick={() => navigate('/accounting')} aria-label="Retour">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Livre journal</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/accounting/journal/new')}>
          Nouvelle écriture
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Rechercher une référence ou une description..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: 280 }}
        />
        <TextField select size="small" label="Exercice" value={yearFilter} onChange={(e) => setYearFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="all">Tous</MenuItem>
          {years.map((y) => (
            <MenuItem key={y} value={y}>{y}</MenuItem>
          ))}
        </TextField>
        <TextField select size="small" label="Statut" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ minWidth: 140 }}>
          <MenuItem value="all">Tous</MenuItem>
          <MenuItem value="brouillon">Brouillon</MenuItem>
          <MenuItem value="validé">Validé</MenuItem>
        </TextField>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Date</strong></TableCell>
              <TableCell><strong>Référence</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="right"><strong>Débit</strong></TableCell>
              <TableCell align="right"><strong>Crédit</strong></TableCell>
              <TableCell><strong>Source</strong></TableCell>
              <TableCell><strong>Statut</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && filtered.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  Aucune écriture pour ces filtres.
                </TableCell>
              </TableRow>
            )}
            {filtered.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell>{formatDate(e.date)}</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{e.reference}</TableCell>
                <TableCell>{e.description}</TableCell>
                <TableCell align="right">{Number(e.totalDebit || 0).toFixed(2)} €</TableCell>
                <TableCell align="right">{Number(e.totalCredit || 0).toFixed(2)} €</TableCell>
                <TableCell>
                  <Chip size="small" label={e.source === 'mollie' ? 'Mollie' : 'Manuel'} variant="outlined" />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={e.status === 'validé' ? 'Validé' : 'Brouillon'}
                    color={e.status === 'validé' ? 'success' : 'default'}
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={e.status === 'validé' ? 'Consulter' : 'Modifier'}>
                    <IconButton size="small" onClick={() => navigate(`/accounting/journal/${e.id}/edit`)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {e.status === 'validé' ? (
                    <Tooltip title="Dévalider pour corriger">
                      <IconButton size="small" onClick={() => handleUnvalidate(e)}>
                        <LockOpenIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Supprimer">
                      <IconButton size="small" onClick={() => handleDelete(e)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default JournalEntries;
